import { lazy, Suspense } from "react";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { BrowserRouter, Route, Routes } from "react-router-dom";
import { Toaster as Sonner } from "@/components/ui/sonner";
import { Toaster } from "@/components/ui/toaster";
import { TooltipProvider } from "@/components/ui/tooltip";
import Index from "./pages/Index.tsx"; // eager: home
import { AnalyticsTracker } from "./components/AnalyticsTracker";
import { ReferralCapture } from "./components/ReferralCapture";

// Public (lazy)
const Devis = lazy(() => import("./pages/Devis.tsx"));
const Services = lazy(() => import("./pages/Services.tsx"));
const ServiceDetail = lazy(() => import("./pages/ServiceDetail.tsx"));
const MicroDetail = lazy(() => import("./pages/MicroDetail.tsx"));
const Microservices = lazy(() => import("./pages/Microservices.tsx"));
const Packs = lazy(() => import("./pages/Packs.tsx"));
const PackDetail = lazy(() => import("./pages/PackDetail.tsx"));
const VilleLanding = lazy(() => import("./pages/VilleLanding.tsx"));
const NotFound = lazy(() => import("./pages/NotFound.tsx"));
const Partenaires = lazy(() => import("./pages/Partenaires.tsx"));
const PlanDuSite = lazy(() => import("./pages/PlanDuSite.tsx"));

// Intent landing pages (SEO/AEO/GEO router)
const TarifsLanding = lazy(() => import("./pages/intent/TarifsLanding.tsx"));
const ComparerLanding = lazy(() => import("./pages/intent/ComparerLanding.tsx"));
const GuideLanding = lazy(() => import("./pages/intent/GuideLanding.tsx"));
const ExpressLanding = lazy(() => import("./pages/intent/ExpressLanding.tsx"));
const VilleServiceLanding = lazy(() => import("./pages/intent/VilleServiceLanding.tsx"));
import { IntentBanner } from "./components/intent/IntentBanner";

// Admin (lazy)
const AdminLogin = lazy(() => import("./pages/admin/AdminLogin.tsx"));
const AdminDashboard = lazy(() => import("./pages/admin/AdminDashboard.tsx"));
const AdminLeads = lazy(() => import("./pages/admin/AdminLeads.tsx"));
const AdminTeam = lazy(() => import("./pages/admin/AdminTeam.tsx"));
const AdminBlog = lazy(() => import("./pages/admin/AdminBlog.tsx"));
const AdminBlogEdit = lazy(() => import("./pages/admin/AdminBlogEdit.tsx"));
const AdminSeo = lazy(() => import("./pages/admin/AdminSeo.tsx"));
const AdminPartners = lazy(() => import("./pages/admin/AdminPartners.tsx"));
const AdminCommissions = lazy(() => import("./pages/admin/AdminCommissions.tsx"));
const AdminServices = lazy(() => import("./pages/admin/AdminServices.tsx"));
const AdminMicroservices = lazy(() => import("./pages/admin/AdminMicroservices.tsx"));
const AdminPacks = lazy(() => import("./pages/admin/AdminPacks.tsx"));
const AdminTestimonials = lazy(() => import("./pages/admin/AdminTestimonials.tsx"));
const AdminLayout = lazy(() =>
  import("./components/admin/AdminLayout.tsx").then((m) => ({ default: m.AdminLayout })),
);
import { ProtectedRoute } from "./components/admin/ProtectedRoute.tsx";

// Légal (lazy)
const Cgv = lazy(() => import("./pages/legal/Cgv.tsx"));
const Confidentialite = lazy(() => import("./pages/legal/Confidentialite.tsx"));
const MentionsLegales = lazy(() => import("./pages/legal/MentionsLegales.tsx"));
const APropos = lazy(() => import("./pages/legal/APropos.tsx"));

// Blog (lazy)
const BlogIndex = lazy(() => import("./pages/blog/BlogIndex.tsx"));
const BlogPost = lazy(() => import("./pages/blog/BlogPost.tsx"));

// OAuth consent (MCP)
const OAuthConsent = lazy(() => import("./pages/OAuthConsent.tsx"));

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 60_000,
      gcTime: 5 * 60_000,
      refetchOnWindowFocus: false,
      retry: 1,
    },
  },
});

const RouteFallback = () => (
  <div className="flex min-h-screen items-center justify-center bg-background">
    <div className="size-8 animate-spin rounded-full border-2 border-primary border-t-transparent" />
  </div>
);

const App = () => (
  <QueryClientProvider client={queryClient}>
    <TooltipProvider>
      <Toaster />
      <Sonner />
      <BrowserRouter>
        <AnalyticsTracker />
        <ReferralCapture />
        <Suspense fallback={null}>
          <IntentBanner />
        </Suspense>
        <Suspense fallback={<RouteFallback />}>
          <Routes>
            <Route path="/" element={<Index />} />
            <Route path="/devis" element={<Devis />} />
            <Route path="/services" element={<Services />} />
            <Route path="/microservices" element={<Microservices />} />
            <Route path="/micro-services" element={<Microservices />} />
            <Route path="/micros" element={<Microservices />} />
            <Route path="/services/:slug" element={<ServiceDetail />} />
            <Route path="/services/:service/:slug" element={<MicroDetail />} />
            <Route path="/packs" element={<Packs />} />
            <Route path="/packs/:slug" element={<PackDetail />} />
            <Route path="/maroc/:ville" element={<VilleLanding />} />
            <Route path="/maroc/:ville/:service" element={<VilleServiceLanding />} />
            <Route path="/partenaires" element={<Partenaires />} />
            <Route path="/devenir-partenaire" element={<Partenaires />} />

            {/* Intent router — landing pages par intention de recherche */}
            <Route path="/tarifs/:service" element={<TarifsLanding />} />
            <Route path="/comparer/:slug" element={<ComparerLanding />} />
            <Route path="/guides/:slug" element={<GuideLanding />} />
            <Route path="/express/:service" element={<ExpressLanding />} />

            {/* Pages légales */}
            <Route path="/cgv" element={<Cgv />} />
            <Route path="/conditions-generales-de-vente" element={<Cgv />} />
            <Route path="/confidentialite" element={<Confidentialite />} />
            <Route path="/politique-de-confidentialite" element={<Confidentialite />} />
            <Route path="/mentions-legales" element={<MentionsLegales />} />
            <Route path="/a-propos" element={<APropos />} />
            <Route path="/qui-sommes-nous" element={<APropos />} />
            <Route path="/plan-du-site" element={<PlanDuSite />} />
            <Route path="/sitemap" element={<PlanDuSite />} />

            {/* Blog public */}
            <Route path="/blog" element={<BlogIndex />} />
            <Route path="/blog/:slug" element={<BlogPost />} />

            {/* Admin (public) */}
            <Route path="/admin/login" element={<AdminLogin />} />

            {/* OAuth consent (MCP server) */}
            <Route path="/.lovable/oauth/consent" element={<OAuthConsent />} />

            {/* Admin (protégé) */}
            <Route path="/admin" element={<ProtectedRoute><AdminLayout /></ProtectedRoute>}>
              <Route index element={<AdminDashboard />} />
              <Route path="dashboard" element={<AdminDashboard />} />
              <Route path="leads" element={<AdminLeads />} />
              <Route path="team" element={<ProtectedRoute requireRole="admin"><AdminTeam /></ProtectedRoute>} />
              <Route path="blog" element={<AdminBlog />} />
              <Route path="blog/new" element={<AdminBlogEdit />} />
              <Route path="blog/:id" element={<AdminBlogEdit />} />
              <Route path="seo" element={<AdminSeo />} />
              <Route path="partners" element={<AdminPartners />} />
              <Route path="commissions" element={<AdminCommissions />} />
              <Route path="services" element={<AdminServices />} />
              <Route path="microservices" element={<AdminMicroservices />} />
              <Route path="packs" element={<AdminPacks />} />
              <Route path="testimonials" element={<AdminTestimonials />} />
            </Route>

            {/* ADD ALL CUSTOM ROUTES ABOVE THE CATCH-ALL "*" ROUTE */}
            <Route path="*" element={<NotFound />} />
          </Routes>
        </Suspense>
      </BrowserRouter>
    </TooltipProvider>
  </QueryClientProvider>
);

export default App;
